// src/components/SwipeCardStack.js
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import StoryCard from "./StoryCard";

// Dummy data for the cards
const CardsData = [
  {
    id: 1,
    mediaUrl: "https://picsum.photos/400/300?random=1",
    isVideo: false,
    description: "A beautiful moment captured in time.",
    themeColor: "#ff69b4",
  },
  {
    id: 2,
    mediaUrl: "https://www.w3schools.com/html/mov_bbb.mp4",
    isVideo: true,
    description: "Our favorite video memory together.",
    themeColor: "#ffc0cb",
  },
  {
    id: 3,
    mediaUrl: "https://picsum.photos/400/300?random=2",
    isVideo: false,
    description: "Cherishing every smile we share.",
    themeColor: "#ff1493",
  },
  {
    id: 4,
    mediaUrl: "https://picsum.photos/400/300?random=3",
    isVideo: false,
    description: "Sunset vibes that warm the heart.",
    themeColor: "#db7093",
  },
  {
    id: 5,
    mediaUrl: "https://www.w3schools.com/html/movie.mp4",
    isVideo: true,
    description: "An unforgettable rainy day adventure. The best kind of day. 🌧️",
    themeColor: "#ff69b4",
  },
];

const SwipeCardStack = ({ cardsData = CardsData }) => {
  // const [cards, setCards] = useState(cardsData);
  const cards = cardsData;
  const [middleCard, setMiddleCard] = useState(0);

  // Move the swiped card to the bottom (i.e., beginning of the array)
  const handleSwipe = (swipedIndex, isLeft) => {
    // If swiped is not the middle card, show the swiped card
    if (swipedIndex !== middleCard) {
      setMiddleCard(swipedIndex);
      return;
    }

    // If the new middle card is out of bounds, go to the start or end
    let newMiddleCard = swipedIndex + (isLeft ? 1 : -1);
    if (newMiddleCard < 0) {
      newMiddleCard = cards.length - 1;
    } else if (newMiddleCard >= cards.length) {
      newMiddleCard = 0;
    }

    setMiddleCard(newMiddleCard);
  };

  // Set card width and height
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const widthHeight = useMemo(() => {
    return isMobile ? {
      height: "460px",
      width: "250px",
    } : {
      height: "480px",
      width: "320px",
    }
  }, [isMobile]);

  return (
    <Box
      sx={{
        position: "relative",
        margin: "auto",
        my: 4,
        ...widthHeight,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" align="center" sx={{ color: "text.secondary" }}>
          {middleCard + 1} / {cards.length}
        </Typography>
      </Box>

      {cards.map((card, index) => {
        // The middle card should be at the top of the stack
        const offset = index - middleCard;
        const maxDisplay = 2;

        // If the card is too far away from the middle card, don't display it
        if (Math.abs(offset) > (maxDisplay + 1)) {
          return null;
        }

        // Calculate a slight rotation for overlap.
        const rotation = offset * 1;
        const x = offset * (isMobile ? 16 : 54);
        const zIndex = cards.length - Math.abs(offset);
        const opacity = Math.abs(offset) > maxDisplay
          ? 0 // (Math.abs(offset) === (maxDisplay + 1) ? 0.5 : 0)
          : 1;

        const isTopCard = index === middleCard;

        return (
          <motion.div
            key={card.id}
            // drag={isTopCard ? "x" : false}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            whileDrag={{ scale: 1.05 }}
            onDragEnd={(_, info) => {
              // If the card is swiped far enough horizontally, move it to the back
              if (Math.abs(info.offset.x) > 100) {
                handleSwipe(index, info.offset.x < 0);
              }
            }}
            initial={{ rotate: rotation, x, zIndex, opacity }}
            animate={{ rotate: rotation, x, zIndex, opacity }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              backgroundColor: card.color,
              borderRadius: "15px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              fontSize: "20px",
              fontWeight: "bold",
              cursor: "grab",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
              transformOrigin: "bottom center", // Pivot at the bottom
            }}
          >
            <StoryCard {...card} isTopCard={isTopCard} isMobile={isMobile} />
          </motion.div>
        );
      })}
    </Box>
  );
};

export default SwipeCardStack;
